import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { CompanyAgentService } from 'src/app/services/company-agent/company-agent.service';
import { CompanyAgentModalService } from 'src/app/services/company-agent-modal/company-agent-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-md-company-agents',
  templateUrl: './md-company-agents.component.html',
  styleUrls: ['./md-company-agents.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class MdCompanyAgentsComponent extends DestructorComponent implements OnInit, AfterViewInit {

  companyAgents: any[] = [];
  isLoading = false;

  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  selectedCompanyAgents: string[] = [];

  constructor(
    private _companyAgentService: CompanyAgentService,
    private _companyAgentModalService: CompanyAgentModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadCompanyAgents();
  }

  ngAfterViewInit(): void { }

  loadCompanyAgents(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedCompanyAgents = [];
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._companyAgentService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.companyAgents = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.companyAgents = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars();
        },
        error: (error) => {
          console.error('Error loading company agents:', error);
          this.companyAgents = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadCompanyAgents(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadCompanyAgents(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadCompanyAgents();
  }

  openAddCompanyAgentModal(): void {
    this._companyAgentModalService.clearSelectedCompanyAgent();
    const modalElement = document.getElementById('kt_modal_company_agent_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(companyAgent: any): void {
    this._companyAgentModalService.setSelectedCompanyAgent(companyAgent);
    const modalElement = document.getElementById('kt_modal_company_agent_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteCompanyAgent(companyAgent: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${companyAgent.name}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(companyAgent);
      }
    });
  }

  private performDelete(companyAgent: any): void {
    const companyAgentId = companyAgent._id || companyAgent.id;
    this.subscriptions.push(
      this._companyAgentService.DeleteDataById(companyAgentId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Company agent has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadCompanyAgents(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete company agent.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting company agent.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onCompanyAgentAdded(): void {
    this.loadCompanyAgents(this.currentPage, this.searchTerm, this.filters);
  }

  onCompanyAgentUpdated(): void {
    this.loadCompanyAgents(this.currentPage, this.searchTerm, this.filters);
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  isSelected(companyAgentId: string): boolean {
    return this.selectedCompanyAgents.includes(companyAgentId);
  }

  isAllSelected(): boolean {
    return this.companyAgents.length > 0 && this.selectedCompanyAgents.length === this.companyAgents.length;
  }

  isIndeterminate(): boolean {
    return this.selectedCompanyAgents.length > 0 && this.selectedCompanyAgents.length < this.companyAgents.length;
  }

  toggleSelection(companyAgentId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedCompanyAgents.includes(companyAgentId)) {
        this.selectedCompanyAgents.push(companyAgentId);
      }
    } else {
      this.selectedCompanyAgents = this.selectedCompanyAgents.filter(id => id !== companyAgentId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedCompanyAgents = this.companyAgents.map(companyAgent => companyAgent._id);
    } else {
      this.selectedCompanyAgents = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-company-agent-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-company-agent-table-toolbar="selected"]');

    if (this.selectedCompanyAgents.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedCompanyAgents(): void {
    if (this.selectedCompanyAgents.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedCompanyAgents.length} selected company agent(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedCompanyAgents.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._companyAgentService.BulkDelete(this.selectedCompanyAgents).subscribe({
        next: () => {
          this.loadCompanyAgents();
          this.selectedCompanyAgents = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected company agents have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting company agents:', error);
          Swal.fire('Error!', 'Failed to delete selected company agents.', 'error');
        }
      })
    );
  }

  Math = Math;
}
